import React from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import { getMarketVolatility } from 'src/redux/modules/marketVolatility/marketVolatility.selectors';
import {
  IRA_WITHHOLDING_WARNING_MODAL,
  openModal,
} from 'src/redux/modules/modals/modals.module';
import { getHasAPP } from 'src/redux/modules/productTypes/productType.selectors';
import {
  getSellWithholdingWarned,
  setProgressFlag,
} from 'src/redux/modules/progress';
import { setShowAllFundsStep } from 'src/redux/modules/quickBuy/quickBuy.module';
import { setShowAllSellFunds } from 'src/redux/modules/quickSell/quickSell.module';
import { getDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';
import {
  deselectSellFund,
  selectSellFund,
  updateSellFund,
  updateSellFundAmount,
  updateSellFundUnit,
} from 'src/redux/modules/sellTransaction/sellTransaction.module';
import {
  getFilteredSellFundsList,
  getHasNotInheritedSEPOrSimpleIRA,
  getIsQuickSellWithoutZeroBalance,
  getQuickSellAgreementID,
  getTransactionLimit,
  getValidatedSellFundsList,
  getAutoSellEditFund,
} from 'src/redux/modules/sellTransaction/sellTransaction.selectors';
import {
  getHiddenAccountNumber,
  getRMData,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import {
  getPersonalInformationData,
  getUserAddress,
} from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import { isRetirementFund } from 'src/utils/funds/funds-utility';
import {
  isAutomaticSell,
  isAutomaticSellEdit,
} from 'src/utils/transactions-util';
import { strictMemoize } from 'src/utils/utils';
import { AutoSellFundEditor } from '../../AutoSell';
import ExpandedFundList from '../../Buy/BuyFlow/ExpandedFundList/ExpandedFundList';
import StepExpanded from '../../Shared/StepExpanded/StepExpanded';
import SellFundEditor from '../SellFundEditor/SellFundEditor';
import './SellFundList.scss';
import { isCustomerWebViewEnabled } from '../../../redux/modules/authentication/authentication.module.selectors';
import { isSalesforceRep } from '../../../redux/modules/authentication/authentication.module.selectors';
import { DELIVERY_IN_KIND, AGREEMENT_TYPES } from '../../../settings/settings';
import { getMandatoryWithholdingStates } from 'src/redux/modules/stateWithholding/selectors';

/**
 * Given the actions object, it creates a custom selectFund action creator that calls the underlying
 * selectSellFund action creator and also opens the IRA withholding modal if appropriate.
 *
 * To implement this functionality we need a combination of data: the fund that is being selected, the progress
 * flag for if we've already warned the user, and we need the open modal action.
 *
 * If we try to put this code in the selectSellFund action creator directly, we run into issues of not having
 * access to the flags or open modal action creator. We can't effectively use a class method as is used for most
 * callbacks because here we need to embed our method in the actions object that is passed downstream.
 *
 * The memoize is used to avoid changing the props for ExpandedFundList on every render with a new reference to
 * a different actions object that is essential the same actions objects, just recreated with the same methods.
 *
 * @summary Modifies the actions object to add a selectFund implementation with IRA withholding modal functionality
 *
 * @type {Function}
 *
 */

const augmentSelectSellFundCreatorMemoized = strictMemoize(
  function augmentSelectSellFundCreator(
    customerWebViewEnabled,
    isSalesforceRep,
    actions,
    sellWithholdingWarned,
    deliveryMethod,
    mandatoryWithholdingStates
  ) {
    return {
      ...actions,
      selectFund: function augmentSelectSellFund(agreementID, fund) {
        actions.selectSellFund(agreementID);
        actions.updateSellFund({
          agreementID,
          trpCode: fund.trpCode,
        });
        if (deliveryMethod !== DELIVERY_IN_KIND && isRetirementFund(fund)) {
          actions.openModal(IRA_WITHHOLDING_WARNING_MODAL, {
            agreementID,
            showPageIraTable: true,
            userState: fund?.address?.state,
            mandatoryWithholdingStates: mandatoryWithholdingStates,
            isRoth:
              fund.agreementType === AGREEMENT_TYPES.ROTH_IRA ||
              fund.agreementType === AGREEMENT_TYPES.ROTH_ROLLOVER_IRA,
          });
        }
      },
    };
  }
);

export interface SellFundListProps {
  customerWebViewEnabled: boolean;
  isSalesforceRep: boolean;
  actions?;
  quickSell?;
  userFunds?;
  userStateWithholdingData?;
  currentStep: number;
}

const SellFundList: React.VFC<SellFundListProps> = ({
  customerWebViewEnabled,
  isSalesforceRep,
  actions,
  quickSell: { showAllSellFunds },
  userFunds,
  userStateWithholdingData,
  currentStep,
}) => {
  // const isSalesforceRepEnabled = useSelector(isSalesforceRep);
  // const customerWebViewEnabled = useSelector(isCustomerWebViewEnabled);
  const deliveryMethod = useSelector(getDeliveryMethod);
  const validatedSellFundsList = useSelector(getValidatedSellFundsList);
  const sellEditFund = useSelector(getAutoSellEditFund);
  const filteredSellFundsList = useSelector(getFilteredSellFundsList);
  const isQuickSellWithoutZeroBalance = useSelector(
    getIsQuickSellWithoutZeroBalance
  );
  const hasAPP: boolean = useSelector(getHasAPP);
  const hasNotInheritedSEPOrSimpleIRA: boolean = useSelector(
    getHasNotInheritedSEPOrSimpleIRA
  );
  const hiddenAccountsNumber = useSelector(getHiddenAccountNumber);
  const marketVolatility = useSelector(getMarketVolatility);
  const personalData = useSelector(getPersonalInformationData);
  const quickSellAgreementID = useSelector(getQuickSellAgreementID);
  const RMData = useSelector(getRMData);
  const selectedBank = useSelector(getSelectedBank);
  const sellWithholdingWarned = useSelector(getSellWithholdingWarned);
  const transactionLimit = useSelector(getTransactionLimit);
  const userAddress = useSelector(getUserAddress);
  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);

  return (
    <StepExpanded>
      <ExpandedFundList
        {...{
          isSalesforceRep,
          customerWebViewEnabled,
          currentStep,
          deliveryMethod,
          hasAPP,
          hasNotInheritedSEPOrSimpleIRA,
          hiddenAccountsNumber,
          isQuickSellWithoutZeroBalance,
          marketVolatility,
          personalData,
          quickSellAgreementID,
          RMData,
          selectedBank,
          showAllSellFunds,
          transactionLimit,
          userAddress,
          userFunds,
          userStateWithholdingData,
        }}
        IRALimit={{ data: { currentYearRemainingContributions: null } }}
        actions={augmentSelectSellFundCreatorMemoized(
          customerWebViewEnabled,
          isSalesforceRep,
          actions,
          sellWithholdingWarned,
          deliveryMethod,
          mandatoryWithholdingStates
        )}
        availableFunds={
          isAutomaticSellEdit() ? sellEditFund : validatedSellFundsList
        }
        currentYearInclusiveRemainingContributions={null}
        customerWebViewEnabled={customerWebViewEnabled}
        fundEditor={() =>
          isAutomaticSell() ? AutoSellFundEditor : SellFundEditor
        }
        isManySelect={false}
        isSalesforceRep={isSalesforceRep}
        numberOfFunds={isAutomaticSellEdit() ? 1 : filteredSellFundsList.length}
        priorYearInclusiveRemainingContributions={null}
        quickBuy={{}}
        setShowAllSellFunds={actions.setShowAllSellFunds}
        shoppingCart={{}}
      />
    </StepExpanded>
  );
};

const mapStateToProps = (state) => {
  const { userFunds, quickSell } = state;
  return {
    isSalesforceRep: isSalesforceRep(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    quickSell,
    userFunds,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      // We rename some cell methods so shared components can use buy or sell methods interchangeably
      deselectFund: deselectSellFund,
      openModal,
      selectSellFund,
      setProgressFlag,
      setShowAllFundsStep,
      updateSellFund,
      updateSellFundAmount,
      updateSellFundUnit,
      setShowAllSellFunds,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(SellFundList);
